<script setup lang="ts">
import type { PropType } from 'vue'
import { ref, watch } from 'vue'

const props = defineProps({
  label: {
    type: String,
    default: '',
  },
  id: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    default: '',
  },
  checked: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String as PropType<'checkbox' | 'radio'>,
    default: 'checkbox',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  (e: 'input', value: boolean): void
}>()

const isChecked = ref(props.checked)

watch(
  () => props.checked,
  (value: boolean) => {
    isChecked.value = value
  },
)
const onChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  isChecked.value = target.checked
  emit('input', target.checked)
}
</script>

<template>
  <label class="adn-switch-button" :class="{ 'adn-switch-button--checked': isChecked }" @change.stop>
    <span class="adn-switch-button__label">{{ label }}</span>
    <input
      :id="id"
      :type="type"
      class="adn-switch-button__input"
      :name="name"
      :checked="isChecked"
      :disabled="disabled"
      @input="onChange"
    />
  </label>
</template>

<style lang="scss" scoped>
.adn-switch-button {
  $root: &;

  position: relative;
  display: flex;
  flex-direction: row;
  gap: var(--spacing-2);
  align-items: center;
  padding: var(--spacing-2) var(--spacing-3);
  color: var(--color-black);
  font-weight: 400;
  font-size: var(--font-size-s);
  line-height: var(--line-height-s);
  background-color: var(--color-white);
  border: 1px solid var(--color-grey-light);
  border-radius: 10rem;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  white-space: nowrap;

  &:hover {
    border: 1px solid var(--color-grey-dark);
  }

  &:has(> &__input:checked) {
    color: var(--color-white);
    background-color: var(--color-main-dark);

    &:hover {
      border: 1px solid var(--color-main-dark);
    }
  }

  &:has(> &__input:disabled) {
    color: var(--color-grey-light);
    background-color: var(--color-grey-lighter);
    pointer-events: none;
  }

  &__input {
    position: absolute;
    margin: 0;
    opacity: 0;
  }

  &--checked {
    color: var(--color-white);
    background-color: var(--color-main-dark);
  }
}
</style>
